<template>
    <div class="vrm-filter_dropdown">
        <div
            class="vrm-filter_dropdown_label"
            :class="{ 'vrm-filter_dropdown_focus': isOpen }"
            @click="toggle()"
        >
            <div class="vrm-filter_dropdown_label_and_count">
                {{
                    filter.i18n
                        ? $t(filter.i18n, {
                              i18nFallback: filter.label,
                          })
                        : filter.label
                }}
                <span v-if="numberOfFilters !== 0" class="vrm-filter_dropdown_label__count">
                    {{ numberOfFilters }}
                </span>
            </div>
            <i
                class="sg-vrm-icon_141_arrow-chevron-down-16px"
                :class="{ 'rotate-arrow': isOpen }"
            ></i>
        </div>
        <div v-if="isOpen" class="vrm-filter_options">
            <div v-for="option in filter.options" :key="option.id">
                <vrm-installation-overview-filter-check
                    v-if="option.type === 'check'"
                    :options="option.options"
                    @submit="onApply"
                ></vrm-installation-overview-filter-check>
                <vrm-installation-overview-filter-tags
                    v-if="option.type === 'tags'"
                    :label="option.label"
                    :i18n="option.i18n"
                    :options="option.options"
                    @submit="onApply"
                ></vrm-installation-overview-filter-tags>
                <vrm-installation-overview-filter-tag-search
                    v-if="option.type === 'tagSearch'"
                    :label="option.label"
                    :i18n="option.i18n"
                    :options="option.options"
                    @submit="onApply"
                ></vrm-installation-overview-filter-tag-search>
                <vrm-installation-overview-filter-radio
                    v-if="option.type === 'radio'"
                    :value-selected="option.selected"
                    :options="option.options"
                    :variable="option.variable"
                    @selected="selected => (option.selected = selected)"
                    @submit="onApply"
                ></vrm-installation-overview-filter-radio>
                <vrm-installation-overview-filter-min-max
                    v-if="option.type === 'minMax' || option.type === 'minMaxString'"
                    :model-min="option.min"
                    :model-max="option.max"
                    :label="option.label"
                    :i18n="option.i18n"
                    :measure="option.measure"
                    :input-type="option.type === 'minMaxString' ? 'text' : 'number'"
                    @update-min="min => (option.min = min)"
                    @update-max="max => (option.max = max)"
                    @submit="onApply"
                ></vrm-installation-overview-filter-min-max>
                <vrm-installation-overview-filter-min-max-combo
                    v-if="option.type === 'minMaxCombo'"
                    :model-min="option.min"
                    :model-max="option.max"
                    :options="option.options"
                    :i18n="option.i18n"
                    @update-min="min => (option.min = min)"
                    @update-max="max => (option.max = max)"
                    @submit="onApply"
                ></vrm-installation-overview-filter-min-max-combo>
                <vrm-installation-overview-filter-text-search
                    v-if="option.type === 'textSearch'"
                    v-model="option.value"
                    :label="option.label"
                    :i18n="option.i18n"
                    @submit="onApply"
                ></vrm-installation-overview-filter-text-search>
            </div>
            <div class="vrm-filter-buttons-container">
                <button class="sg-btn sg-alt" @click="onClear()">
                    {{
                        $t('installationoverview.clear', {
                            i18nFallback: 'Clear',
                        })
                    }}
                </button>
                <button
                    id="confirm-filter"
                    :class="{ 'hide-button': hideApply }"
                    class="sg-btn sg-blue"
                    @click="
                        () => {
                            onApply();
                            triggerGoogleAnalyticsEvent();
                        }
                    "
                >
                    {{
                        $t('installationoverview.apply', {
                            i18nFallback: 'Apply',
                        })
                    }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import VrmInstallationOverviewFilterCheck from '@/vue/components/shared/VrmInstallationOverviewFilterCheck';
import VrmInstallationOverviewFilterRadio from '@/vue/components/shared/VrmInstallationOverviewFilterRadio';
import VrmInstallationOverviewFilterMinMax from '@/vue/components/shared/VrmInstallationOverviewFilterMinMax';
import VrmInstallationOverviewFilterMinMaxCombo from '@/vue/components/shared/VrmInstallationOverviewFilterMinMaxCombo';
import VrmInstallationOverviewFilterTags from '@/vue/components/shared/VrmInstallationOverviewFilterTags';
import VrmInstallationOverviewFilterTagSearch from '@/vue/components/shared/VrmInstallationOverviewFilterTagSearch';
import VrmInstallationOverviewFilterTextSearch from '@/vue/components/shared/VrmInstallationOverviewFilterTextSearch.vue';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({
    components: {
        VrmInstallationOverviewFilterCheck,
        VrmInstallationOverviewFilterMinMax,
        VrmInstallationOverviewFilterMinMaxCombo,
        VrmInstallationOverviewFilterRadio,
        VrmInstallationOverviewFilterTags,
        VrmInstallationOverviewFilterTextSearch,
        VrmInstallationOverviewFilterTagSearch,
    },
})
@AngularLocaleBundle('installationoverview')
@AngularLocaleBundle('tags')
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('core')
@AngularDirective('vrm-installations-overview-filter-dropdown')
export default class VrmInstallationOverviewFilterDropdown extends Vue {
    @Prop({ type: Object, default: '' })
    filter;

    @Prop({ type: String, default: '' })
    filterId;

    isOpen = false;

    clickOutsideOffDropdown(e) {
        if (e.target instanceof HTMLElement && !this.$el.contains(e.target)) {
            this.isOpen = false;
        }
    }

    mounted() {
        document.addEventListener('click', this.clickOutsideOffDropdown);
    }

    beforeDestroy() {
        document.removeEventListener('click', this.clickOutsideOffDropdown);
    }

    toggle() {
        this.isOpen = !this.isOpen;
    }

    onClear() {
        this.$emit('clear', this.filterId);
    }

    onApply() {
        this.$emit('apply');
    }

    get hideApply() {
        return !this.filter.options.some(option => {
            return option.type !== 'tags' && option.type !== 'tagSearch';
        });
    }

    get numberOfFilters() {
        let count = 0;
        this.filter.options.forEach(option => {
            if (option.type === 'check' || option.type === 'tags' || option.type === 'tagSearch') {
                option.options.forEach(check => check.value && count++);
            } else if (option.type === 'radio') {
                option.selected !== '' && count++;
            } else if (
                option.type === 'minMax' ||
                option.type === 'minMaxString' ||
                option.type === 'minMaxCombo'
            ) {
                option.min !== '' && count++;
                option.max !== '' && count++;
            } else if (option.type === 'textSearch') {
                option.value !== '' && count++;
            }
        });
        return count;
    }

    triggerGoogleAnalyticsEvent() {
        // Send event to Google Analytics
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'InstallationOverviewApplyFilterButtonClicked',
            page_path: window.location.pathname,
            page_title: document.title,
        });
    }
}
</script>
